import { CodeIcon, ExternalLinkIcon } from "../../components/ui/Icons";
import { CodeBlock, Note } from "./shared";

export function TechnicalReferenceDocs() {
  return (
    <>
      {/* Reference Overview */}
      <section id="reference-overview" className="mb-16 scroll-mt-28">
        <h2 className="flex items-center gap-3 font-display text-2xl font-bold text-primary">
          <CodeIcon size={24} className="text-amber" />
          Technical Reference
        </h2>
        <p className="mt-4 text-lg text-muted">
          Complete technical reference for Claude Code plugin system, including schemas, CLI
          commands, and component specifications.
        </p>
        <div className="mt-6">
          <Note>
            Looking to install plugins? See the official docs for{" "}
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/discover-plugins"
              className="text-amber underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Discover and install plugins
            </a>
            . For creating plugins, see{" "}
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/plugins"
              className="text-amber underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Plugins
            </a>
            . For distributing plugins, see{" "}
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/plugin-marketplaces"
              className="text-amber underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Plugin marketplaces
            </a>
            .
          </Note>
        </div>
      </section>

      {/* Plugin Components Reference */}
      <section id="components-reference" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">
          Plugin Components Reference
        </h2>
        <p className="mt-4 text-secondary">
          This section documents the six types of components that plugins can provide.
        </p>

        {/* Commands */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">Commands</h3>
        <p className="mt-2 text-secondary">
          Plugins add custom slash commands that integrate seamlessly with Claude Code's command
          system.
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-secondary">
          <li>
            <strong>Location:</strong>{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
              commands/
            </code>{" "}
            directory in plugin root
          </li>
          <li>
            <strong>File format:</strong> Markdown files with frontmatter
          </li>
        </ul>

        {/* Agents */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">Agents</h3>
        <p className="mt-2 text-secondary">
          Plugins can provide specialized subagents for specific tasks that Claude can invoke
          automatically when appropriate.
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-secondary">
          <li>
            <strong>Location:</strong>{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
              agents/
            </code>{" "}
            directory in plugin root
          </li>
          <li>
            <strong>File format:</strong> Markdown files describing agent capabilities
          </li>
        </ul>
        <div className="mt-4">
          <CodeBlock
            language="markdown"
            code={`---
description: What this agent specializes in
capabilities: ["task1", "task2", "task3"]
---

# Agent Name

Detailed description of the agent's role, expertise, and when Claude should invoke it.

## Capabilities
- Specific task the agent excels at
- Another specialized capability
- When to use this agent vs others

## Context and examples
Provide examples of when this agent should be used and what kinds of problems it solves.`}
          />
        </div>
        <p className="mt-4 text-sm text-muted">
          <strong>Integration points:</strong> Agents appear in the{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">/agents</code>{" "}
          interface, can be invoked automatically by Claude based on task context, or manually by
          users.
        </p>

        {/* Skills */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">Skills</h3>
        <p className="mt-2 text-secondary">
          Plugins can provide Agent Skills that extend Claude's capabilities. Skills are
          model-invoked—Claude autonomously decides when to use them based on the task context.
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-secondary">
          <li>
            <strong>Location:</strong>{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
              skills/
            </code>{" "}
            directory in plugin root
          </li>
          <li>
            <strong>File format:</strong> Directories containing{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
              SKILL.md
            </code>{" "}
            files with frontmatter
          </li>
        </ul>
        <div className="mt-4">
          <CodeBlock
            language="plaintext"
            code={`skills/
├── pdf-processor/
│   ├── SKILL.md
│   ├── reference.md (optional)
│   └── scripts/ (optional)
└── code-reviewer/
    └── SKILL.md`}
          />
        </div>

        {/* Hooks */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">Hooks</h3>
        <p className="mt-2 text-secondary">
          Plugins can provide event handlers that respond to Claude Code events automatically.
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-secondary">
          <li>
            <strong>Location:</strong>{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
              hooks/hooks.json
            </code>{" "}
            in plugin root, or inline in plugin.json
          </li>
          <li>
            <strong>Format:</strong> JSON configuration with event matchers and actions
          </li>
        </ul>
        <div className="mt-4">
          <CodeBlock
            code={`{
  "hooks": {
    "PostToolUse": [
      {
        "matcher": "Write|Edit",
        "hooks": [
          {
            "type": "command",
            "command": "\${CLAUDE_PLUGIN_ROOT}/scripts/format-code.sh"
          }
        ]
      }
    ]
  }
}`}
          />
        </div>
        <p className="mt-4 text-sm text-secondary">
          <strong>Available events:</strong>{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">PreToolUse</code>,{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">PostToolUse</code>,{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
            PostToolUseFailure
          </code>
          ,{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
            PermissionRequest
          </code>
          ,{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">UserPromptSubmit</code>
          , <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">Notification</code>,{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">Stop</code>,{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">SubagentStart</code>,{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">SubagentStop</code>,{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">SessionStart</code>,{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">SessionEnd</code>,{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">PreCompact</code>
        </p>
        <p className="mt-2 text-sm text-secondary">
          <strong>Hook types:</strong>{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">command</code> (execute
          shell commands),{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">prompt</code> (evaluate
          with LLM), <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">agent</code>{" "}
          (run agentic verifier)
        </p>

        {/* MCP Servers */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">MCP Servers</h3>
        <p className="mt-2 text-secondary">
          Plugins can bundle Model Context Protocol (MCP) servers to connect Claude Code with
          external tools and services.
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-secondary">
          <li>
            <strong>Location:</strong>{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
              .mcp.json
            </code>{" "}
            in plugin root, or inline in plugin.json
          </li>
          <li>
            <strong>Format:</strong> Standard MCP server configuration
          </li>
        </ul>
        <div className="mt-4">
          <CodeBlock
            code={`{
  "mcpServers": {
    "plugin-database": {
      "command": "\${CLAUDE_PLUGIN_ROOT}/servers/db-server",
      "args": ["--config", "\${CLAUDE_PLUGIN_ROOT}/config.json"],
      "env": {
        "DB_PATH": "\${CLAUDE_PLUGIN_ROOT}/data"
      }
    },
    "plugin-api-client": {
      "command": "npx",
      "args": ["@company/mcp-server", "--plugin-mode"],
      "cwd": "\${CLAUDE_PLUGIN_ROOT}"
    }
  }
}`}
          />
        </div>

        {/* LSP Servers */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">LSP Servers</h3>
        <p className="mt-2 text-secondary">
          Plugins can provide Language Server Protocol (LSP) servers to give Claude real-time code
          intelligence while working on your codebase.
        </p>
        <p className="mt-2 text-secondary">
          LSP integration provides instant diagnostics, code navigation (go to definition, find
          references), and language awareness (type information and documentation).
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-secondary">
          <li>
            <strong>Location:</strong>{" "}
            <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
              .lsp.json
            </code>{" "}
            in plugin root, or inline in plugin.json
          </li>
        </ul>
        <div className="mt-4">
          <CodeBlock
            code={`{
  "go": {
    "command": "gopls",
    "args": ["serve"],
    "extensionToLanguage": {
      ".go": "go"
    }
  }
}`}
          />
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Required Field</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">command</td>
                <td className="py-3">The LSP binary to execute (must be in PATH)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">extensionToLanguage</td>
                <td className="py-3">Maps file extensions to language identifiers</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <Note type="warning">
            <strong>You must install the language server binary separately.</strong> LSP plugins
            configure how Claude Code connects to a language server, but they don't include the
            server itself.
          </Note>
        </div>
      </section>

      {/* Installation Scopes */}
      <section id="installation-scopes" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Plugin Installation Scopes</h2>
        <p className="mt-4 text-secondary">
          When you install a plugin, you choose a scope that determines where the plugin is
          available and who else can use it:
        </p>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Scope</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Settings File</th>
                <th className="py-3 text-left font-mono text-muted">Use Case</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">user</td>
                <td className="py-3 pr-4">
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                    ~/.claude/settings.json
                  </code>
                </td>
                <td className="py-3">Personal plugins available across all projects (default)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">project</td>
                <td className="py-3 pr-4">
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                    .claude/settings.json
                  </code>
                </td>
                <td className="py-3">Team plugins shared via version control</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">local</td>
                <td className="py-3 pr-4">
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                    .claude/settings.local.json
                  </code>
                </td>
                <td className="py-3">Project-specific plugins, gitignored</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">managed</td>
                <td className="py-3 pr-4">
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                    managed-settings.json
                  </code>
                </td>
                <td className="py-3">Enterprise-managed plugins (read-only, update only)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Plugin Manifest Schema */}
      <section id="manifest-schema" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Plugin Manifest Schema</h2>
        <p className="mt-4 text-secondary">
          The{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
            plugin.json
          </code>{" "}
          file defines your plugin's metadata and configuration.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Complete Schema</h3>
        <div className="mt-4">
          <CodeBlock
            code={`{
  "name": "plugin-name",
  "version": "1.2.0",
  "description": "Brief plugin description",
  "author": {
    "name": "Author Name",
    "email": "author@example.com",
    "url": "https://github.com/author"
  },
  "homepage": "https://docs.example.com/plugin",
  "repository": "https://github.com/author/plugin",
  "license": "MIT",
  "keywords": ["keyword1", "keyword2"],
  "commands": ["./custom/commands/special.md"],
  "agents": "./custom/agents/",
  "skills": "./custom/skills/",
  "hooks": "./config/hooks.json",
  "mcpServers": "./mcp-config.json",
  "outputStyles": "./styles/",
  "lspServers": "./.lsp.json"
}`}
          />
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Required Fields</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Field</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Type</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">name</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Unique identifier (kebab-case, no spaces)</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Metadata Fields</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Field</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Type</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">version</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Semantic version (e.g., "2.1.0")</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">description</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Brief explanation of plugin purpose</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">author</td>
                <td className="py-3 pr-4">object</td>
                <td className="py-3">Author information (name, email, url)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">homepage</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Documentation URL</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">repository</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">Source code URL</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">license</td>
                <td className="py-3 pr-4">string</td>
                <td className="py-3">License identifier (MIT, Apache-2.0)</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">keywords</td>
                <td className="py-3 pr-4">array</td>
                <td className="py-3">Discovery tags</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Component Path Fields</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Field</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Type</th>
                <th className="py-3 text-left font-mono text-muted">Description</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">commands</td>
                <td className="py-3 pr-4">string | array</td>
                <td className="py-3">Additional command files/directories</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">agents</td>
                <td className="py-3 pr-4">string | array</td>
                <td className="py-3">Additional agent files</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">skills</td>
                <td className="py-3 pr-4">string | array</td>
                <td className="py-3">Additional skill directories</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">hooks</td>
                <td className="py-3 pr-4">string | object</td>
                <td className="py-3">Hook config path or inline config</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">mcpServers</td>
                <td className="py-3 pr-4">string | object</td>
                <td className="py-3">MCP config path or inline config</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">lspServers</td>
                <td className="py-3 pr-4">string | object</td>
                <td className="py-3">LSP config for code intelligence</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-6">
          <Note>
            <strong>Path behavior:</strong> Custom paths supplement default directories - they don't
            replace them. If <code>commands/</code> exists, it's loaded in addition to custom
            command paths. All paths must be relative and start with <code>./</code>.
          </Note>
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Environment Variables</h3>
        <p className="mt-2 text-secondary">
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
            {"${CLAUDE_PLUGIN_ROOT}"}
          </code>
          : Contains the absolute path to your plugin directory. Use this in hooks, MCP servers, and
          scripts to ensure correct paths regardless of installation location.
        </p>
      </section>

      {/* Caching and File Resolution */}
      <section id="caching-resolution" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">
          Plugin Caching and File Resolution
        </h2>
        <p className="mt-4 text-secondary">
          For security and verification purposes, Claude Code copies plugins to a cache directory
          rather than using them in-place.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-secondary">How Plugin Caching Works</h3>
        <p className="mt-2 text-secondary">
          When you install a plugin, Claude Code copies the plugin files to a cache directory:
        </p>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-secondary">
          <li>
            <strong>For marketplace plugins with relative paths:</strong> The path specified in the{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">source</code> field
            is copied recursively.
          </li>
          <li>
            <strong>
              For plugins with{" "}
              <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                .claude-plugin/plugin.json
              </code>
              :
            </strong>{" "}
            The implicit root directory is copied recursively.
          </li>
        </ul>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Path Traversal Limitations</h3>
        <p className="mt-2 text-secondary">
          Plugins cannot reference files outside their copied directory structure. Paths that
          traverse outside the plugin root (such as{" "}
          <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">../shared-utils</code>)
          will not work after installation.
        </p>

        <h3 className="mt-8 text-lg font-semibold text-secondary">
          Working with External Dependencies
        </h3>
        <p className="mt-2 text-secondary">
          If your plugin needs to access files outside its directory, you have two options:
        </p>
        <p className="mt-4 text-secondary">
          <strong>Option 1: Use symlinks</strong>
        </p>
        <p className="mt-2 text-secondary">
          Create symbolic links to external files within your plugin directory. Symlinks are honored
          during the copy process.
        </p>
        <div className="mt-4">
          <CodeBlock
            language="bash"
            code={`# Inside your plugin directory
ln -s /path/to/shared-utils ./shared-utils`}
          />
        </div>
        <p className="mt-4 text-secondary">
          <strong>Option 2: Restructure your marketplace</strong>
        </p>
        <p className="mt-2 text-secondary">
          Set the plugin path to a parent directory that contains all required files.
        </p>
      </section>

      {/* Directory Structure */}
      <section id="directory-structure" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">Plugin Directory Structure</h2>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Standard Plugin Layout</h3>
        <div className="mt-4">
          <CodeBlock
            language="plaintext"
            code={`enterprise-plugin/
├── .claude-plugin/           # Metadata directory
│   └── plugin.json          # Required: plugin manifest
├── commands/                 # Default command location
│   ├── status.md
│   └── logs.md
├── agents/                   # Default agent location
│   ├── security-reviewer.md
│   ├── performance-tester.md
│   └── compliance-checker.md
├── skills/                   # Agent Skills
│   ├── code-reviewer/
│   │   └── SKILL.md
│   └── pdf-processor/
│       ├── SKILL.md
│       └── scripts/
├── hooks/                    # Hook configurations
│   ├── hooks.json           # Main hook config
│   └── security-hooks.json  # Additional hooks
├── .mcp.json                # MCP server definitions
├── .lsp.json                # LSP server configurations
├── scripts/                 # Hook and utility scripts
│   ├── security-scan.sh
│   ├── format-code.py
│   └── deploy.js
├── LICENSE                  # License file
└── CHANGELOG.md             # Version history`}
          />
        </div>

        <div className="mt-6">
          <Note type="warning">
            The <code>.claude-plugin/</code> directory contains the <code>plugin.json</code> file.
            All other directories (commands/, agents/, skills/, hooks/) must be at the plugin root,
            not inside <code>.claude-plugin/</code>.
          </Note>
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">File Locations Reference</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Component</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Default Location</th>
                <th className="py-3 text-left font-mono text-muted">Purpose</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">Manifest</td>
                <td className="py-3 pr-4">
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                    .claude-plugin/plugin.json
                  </code>
                </td>
                <td className="py-3">Required metadata file</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">Commands</td>
                <td className="py-3 pr-4">
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                    commands/
                  </code>
                </td>
                <td className="py-3">Slash command Markdown files</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">Agents</td>
                <td className="py-3 pr-4">
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">agents/</code>
                </td>
                <td className="py-3">Subagent Markdown files</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">Skills</td>
                <td className="py-3 pr-4">
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">skills/</code>
                </td>
                <td className="py-3">Agent Skills with SKILL.md files</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">Hooks</td>
                <td className="py-3 pr-4">
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                    hooks/hooks.json
                  </code>
                </td>
                <td className="py-3">Hook configuration</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">MCP servers</td>
                <td className="py-3 pr-4">
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                    .mcp.json
                  </code>
                </td>
                <td className="py-3">MCP server definitions</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">LSP servers</td>
                <td className="py-3 pr-4">
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                    .lsp.json
                  </code>
                </td>
                <td className="py-3">Language server configurations</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* CLI Commands Reference */}
      <section id="cli-commands" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">CLI Commands Reference</h2>
        <p className="mt-4 text-secondary">
          Claude Code provides CLI commands for non-interactive plugin management, useful for
          scripting and automation.
        </p>

        {/* plugin install */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">plugin install</h3>
        <p className="mt-2 text-secondary">Install a plugin from available marketplaces.</p>
        <div className="mt-4">
          <CodeBlock language="bash" code="claude plugin install <plugin> [options]" />
        </div>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Option</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Description</th>
                <th className="py-3 text-left font-mono text-muted">Default</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4 font-mono text-amber">-s, --scope</td>
                <td className="py-3 pr-4">Installation scope: user, project, or local</td>
                <td className="py-3">user</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <CodeBlock
            language="bash"
            code={`# Install to user scope (default)
claude plugin install formatter@my-marketplace

# Install to project scope (shared with team)
claude plugin install formatter@my-marketplace --scope project

# Install to local scope (gitignored)
claude plugin install formatter@my-marketplace --scope local`}
          />
        </div>

        {/* plugin uninstall */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">plugin uninstall</h3>
        <p className="mt-2 text-secondary">Remove an installed plugin.</p>
        <div className="mt-4">
          <CodeBlock language="bash" code="claude plugin uninstall <plugin> [options]" />
        </div>
        <p className="mt-2 text-sm text-muted">
          <strong>Aliases:</strong> remove, rm
        </p>

        {/* plugin enable */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">plugin enable</h3>
        <p className="mt-2 text-secondary">Enable a disabled plugin.</p>
        <div className="mt-4">
          <CodeBlock language="bash" code="claude plugin enable <plugin> [options]" />
        </div>

        {/* plugin disable */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">plugin disable</h3>
        <p className="mt-2 text-secondary">Disable a plugin without uninstalling it.</p>
        <div className="mt-4">
          <CodeBlock language="bash" code="claude plugin disable <plugin> [options]" />
        </div>

        {/* plugin update */}
        <h3 className="mt-8 text-lg font-semibold text-secondary">plugin update</h3>
        <p className="mt-2 text-secondary">Update a plugin to the latest version.</p>
        <div className="mt-4">
          <CodeBlock language="bash" code="claude plugin update <plugin> [options]" />
        </div>
      </section>

      {/* Debugging and Development Tools */}
      <section id="debugging" className="mb-16 scroll-mt-28">
        <h2 className="font-display text-2xl font-bold text-primary">
          Debugging and Development Tools
        </h2>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Debugging Commands</h3>
        <p className="mt-2 text-secondary">
          Use{" "}
          <code className="rounded bg-surface px-1.5 py-0.5 font-mono text-sm text-amber">
            claude --debug
          </code>{" "}
          to see plugin loading details:
        </p>
        <div className="mt-4">
          <CodeBlock language="bash" code="claude --debug" />
        </div>
        <p className="mt-4 text-secondary">This shows:</p>
        <ul className="mt-2 list-disc space-y-1 pl-6 text-secondary">
          <li>Which plugins are being loaded</li>
          <li>Any errors in plugin manifests</li>
          <li>Command, agent, and hook registration</li>
          <li>MCP server initialization</li>
        </ul>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Common Issues</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-elevated">
                <th className="py-3 pr-4 text-left font-mono text-muted">Issue</th>
                <th className="py-3 pr-4 text-left font-mono text-muted">Cause</th>
                <th className="py-3 text-left font-mono text-muted">Solution</th>
              </tr>
            </thead>
            <tbody className="text-secondary">
              <tr className="border-b border-surface">
                <td className="py-3 pr-4">Plugin not loading</td>
                <td className="py-3 pr-4">Invalid plugin.json</td>
                <td className="py-3">
                  Validate JSON syntax with{" "}
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                    claude plugin validate
                  </code>
                </td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4">Commands not appearing</td>
                <td className="py-3 pr-4">Wrong directory structure</td>
                <td className="py-3">Ensure commands/ at root, not in .claude-plugin/</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4">Hooks not firing</td>
                <td className="py-3 pr-4">Script not executable</td>
                <td className="py-3">
                  Run{" "}
                  <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
                    chmod +x script.sh
                  </code>
                </td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4">MCP server fails</td>
                <td className="py-3 pr-4">Missing {"${CLAUDE_PLUGIN_ROOT}"}</td>
                <td className="py-3">Use variable for all plugin paths</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4">Path errors</td>
                <td className="py-3 pr-4">Absolute paths used</td>
                <td className="py-3">All paths must be relative and start with ./</td>
              </tr>
              <tr className="border-b border-surface">
                <td className="py-3 pr-4">LSP "Executable not found"</td>
                <td className="py-3 pr-4">Language server not installed</td>
                <td className="py-3">
                  Install the binary (e.g., npm install -g typescript-language-server)
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Hook Troubleshooting</h3>
        <p className="mt-2 text-secondary">
          <strong>Hook script not executing:</strong>
        </p>
        <ol className="mt-2 list-decimal space-y-1 pl-6 text-secondary">
          <li>
            Check the script is executable:{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
              chmod +x ./scripts/your-script.sh
            </code>
          </li>
          <li>
            Verify the shebang line: First line should be{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">#!/bin/bash</code>
          </li>
          <li>
            Check the path uses{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
              {"${CLAUDE_PLUGIN_ROOT}"}
            </code>
          </li>
          <li>
            Test the script manually:{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
              ./scripts/your-script.sh
            </code>
          </li>
        </ol>

        <p className="mt-4 text-secondary">
          <strong>Hook not triggering on expected events:</strong>
        </p>
        <ol className="mt-2 list-decimal space-y-1 pl-6 text-secondary">
          <li>
            Verify the event name is correct (case-sensitive):{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">PostToolUse</code>,
            not{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">postToolUse</code>
          </li>
          <li>
            Check the matcher pattern matches your tools:{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">
              "matcher": "Write|Edit"
            </code>
          </li>
          <li>
            Confirm the hook type is valid:{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">command</code>,{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">prompt</code>, or{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs">agent</code>
          </li>
        </ol>

        <h3 className="mt-8 text-lg font-semibold text-secondary">Version Management</h3>
        <p className="mt-2 text-secondary">Follow semantic versioning for plugin releases:</p>
        <div className="mt-4">
          <CodeBlock
            code={`{
  "name": "my-plugin",
  "version": "2.1.0"
}`}
          />
        </div>
        <ul className="mt-4 list-disc space-y-1 pl-6 text-secondary">
          <li>
            <strong>MAJOR:</strong> Breaking changes (incompatible API changes)
          </li>
          <li>
            <strong>MINOR:</strong> New features (backward-compatible additions)
          </li>
          <li>
            <strong>PATCH:</strong> Bug fixes (backward-compatible fixes)
          </li>
        </ul>
      </section>

      {/* Reference See Also */}
      <section className="mb-16 rounded-xl border border-elevated bg-surface/30 p-6">
        <h2 className="font-display text-xl font-bold text-primary">Related Resources</h2>
        <ul className="mt-4 space-y-2">
          <li>
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/plugins"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-amber hover:text-amber-hover"
            >
              <ExternalLinkIcon size={14} />
              Plugins - Tutorials and practical usage
            </a>
          </li>
          <li>
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/plugin-marketplaces"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-amber hover:text-amber-hover"
            >
              <ExternalLinkIcon size={14} />
              Plugin marketplaces - Creating and managing marketplaces
            </a>
          </li>
          <li>
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/slash-commands"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-amber hover:text-amber-hover"
            >
              <ExternalLinkIcon size={14} />
              Slash commands - Command development details
            </a>
          </li>
          <li>
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/sub-agents"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-amber hover:text-amber-hover"
            >
              <ExternalLinkIcon size={14} />
              Subagents - Agent configuration and capabilities
            </a>
          </li>
          <li>
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/skills"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-amber hover:text-amber-hover"
            >
              <ExternalLinkIcon size={14} />
              Agent Skills - Extend Claude's capabilities
            </a>
          </li>
          <li>
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/hooks"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-amber hover:text-amber-hover"
            >
              <ExternalLinkIcon size={14} />
              Hooks - Event handling and automation
            </a>
          </li>
          <li>
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/mcp"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-amber hover:text-amber-hover"
            >
              <ExternalLinkIcon size={14} />
              MCP - External tool integration
            </a>
          </li>
          <li>
            <a
              href="https://docs.anthropic.com/en/docs/claude-code/settings"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-amber hover:text-amber-hover"
            >
              <ExternalLinkIcon size={14} />
              Settings - Configuration options for plugins
            </a>
          </li>
        </ul>
      </section>
    </>
  );
}
